import React from "react";
import { css } from "@linaria/core";
import ModalForm from "@ui/Modal/Form";
import Text from "@ui/Text";
import useToasts from "@ui/Toast/useToasts";
import { color } from "@ui";
import { spacings } from "@ui/Theme";
import { useDelete } from "../../../../../api/useCall";
import { useRevalidate } from "../../../../../api/useQuery";
import type { EventDiagnosed } from "../../../../../api/useEvents";
import { useRemoteFaultEvents } from "../../../../../api/useFaultEvents";
import type { Project } from "../../../../../api/useProjects";
import t from "../../../../../i18n";
import { toPath } from "../../../../../router/helpers/path";
import { useNavigate } from "react-router";

const PATH = "/projects/:projectId(uuid)/faultEvents/grouping/:faultEventId(uuid)" as const;
const REVALIDATE_PATH = "/projects/:projectId(uuid)/faultEvents" as const;

interface ModalFormUngroupEventProps {
  project: Project;
  event: EventDiagnosed;
  onRequestClose: () => void;
}

const ModalFormUngroupEvent = ({ project, event, onRequestClose }: ModalFormUngroupEventProps) => {
  const destroyGrouping = useDelete(PATH);
  const revalidate = useRevalidate();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const remoteFaultEvents = useRemoteFaultEvents(project.id);
  const sourceFaultEventNames = React.useMemo(() => {
    const eventNameById = new Map(
      remoteFaultEvents.map(faultEvent => [faultEvent.id, faultEvent.name]),
    );
    return event.sourceFaultEventIds.map(
      (sourceFaultEventId, index) =>
        eventNameById.get(sourceFaultEventId) ??
        t("project.fault_event.modal.form.ungroup_event.summary.source_fallback", index + 1),
    );
  }, [event.sourceFaultEventIds, remoteFaultEvents]);

  const handleSubmit = React.useCallback(async () => {
    try {
      await destroyGrouping({
        routeParams: { projectId: project.id, faultEventId: event.id },
      });
      await revalidate(REVALIDATE_PATH);
      addToast("success", t("project.fault_event.modal.form.ungroup_event.toast.success"));
      onRequestClose();
      void navigate(toPath("/projects/:projectId/fault-events", { projectId: project.id }), {
        replace: true,
      });
      return true;
    } catch (error) {
      addToast("error", t("errors.backend_error"));
      return false;
    }
  }, [addToast, destroyGrouping, event.id, navigate, onRequestClose, project.id, revalidate]);

  return (
    <ModalForm
      className={modalForm}
      onSubmit={handleSubmit}
      onRequestClose={onRequestClose}
      okButtonName={t("project.fault_event.modal.form.ungroup_event.button.name.ok")}
      okButtonVariant="destructive"
    >
      <Text>
        {t(
          "project.fault_event.modal.form.ungroup_event.summary.name",
          <Text type="body1SemiBold" as="span">
            {event.name}
          </Text>,
        )}
      </Text>

      <section className={section}>
        <Text type="body1SemiBold">
          {t("project.fault_event.modal.form.ungroup_event.summary.failures")}
        </Text>
        <ul className={list}>
          {event.metadata.faults.map(fault => (
            <li key={fault.name}>
              <Text>{fault.displayName}</Text>
            </li>
          ))}
        </ul>
      </section>

      <section className={section}>
        <Text type="body1SemiBold">
          {t("project.fault_event.modal.form.ungroup_event.summary.sources")}
        </Text>
        <ul className={list}>
          {sourceFaultEventNames.map((sourceFaultEventName, index) => (
            <li key={event.sourceFaultEventIds[index]}>
              <Text>{sourceFaultEventName}</Text>
            </li>
          ))}
        </ul>
      </section>

      <div className={warning}>
        <Text>{t("project.fault_event.modal.form.ungroup_event.warning.comments_lost")}</Text>
      </div>
    </ModalForm>
  );
};

export default ModalFormUngroupEvent;

const modalForm = css`
  margin: 12px 0;
`;

const section = css`
  display: grid;
  gap: ${spacings.xxs};
`;

const list = css`
  margin: 0;
  padding-left: ${spacings.l};
`;

const warning = css`
  display: grid;
  gap: ${spacings.xxs};
  padding: ${spacings.s};
  border-left: 4px solid ${color.warning};
  background: ${color.warningBackground};
`;
